import React, { useCallback, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, AlertCircleIcon, ClockIcon, FileTextIcon, LockIcon, BrainCircuitIcon } from 'lucide-react';
type Invoice = {
  id: string;
  obligor: string;
  supplier: string;
  amount: number;
  dueDate: string;
  rating: string;
  tenor: number;
  status: 'pending' | 'ai_verified' | 'human_review' | 'approved' | 'flagged' | 'rejected';
  aiScore: number;
  riskFlags: string[];
  complianceChecks: {
    aml: 'pass' | 'fail' | 'pending';
    sanctions: 'pass' | 'fail' | 'pending';
    creditLimit: 'pass' | 'fail' | 'pending';
    concentration: 'pass' | 'fail' | 'pending';
  };
  documents: {
    invoice: boolean;
    po: boolean;
    bol: boolean;
    ucc: boolean;
  };
};
const INVOICES_DATA: Invoice[] = [{
  id: 'INV-10231',
  obligor: 'Walmart Inc.',
  supplier: 'PPI Apparel Group',
  amount: 165113,
  dueDate: '2025-12-28',
  rating: 'AA-',
  tenor: 45,
  status: 'ai_verified',
  aiScore: 94,
  riskFlags: [],
  complianceChecks: {
    aml: 'pass',
    sanctions: 'pass',
    creditLimit: 'pass',
    concentration: 'pass'
  },
  documents: {
    invoice: true,
    po: true,
    bol: true,
    ucc: false
  }
}, {
  id: 'INV-10232',
  obligor: 'Target Corp',
  supplier: 'Multi-Pak Packaging',
  amount: 243500,
  dueDate: '2025-12-15',
  rating: 'A+',
  tenor: 33,
  status: 'pending',
  aiScore: 88,
  riskFlags: ['High concentration'],
  complianceChecks: {
    aml: 'pass',
    sanctions: 'pass',
    creditLimit: 'pending',
    concentration: 'fail'
  },
  documents: {
    invoice: true,
    po: true,
    bol: false,
    ucc: false
  }
}, {
  id: 'INV-10233',
  obligor: 'Amazon',
  supplier: 'AGI Wholesale',
  amount: 512000,
  dueDate: '2026-01-10',
  rating: 'AA',
  tenor: 60,
  status: 'human_review',
  aiScore: 91,
  riskFlags: [],
  complianceChecks: {
    aml: 'pass',
    sanctions: 'pass',
    creditLimit: 'pass',
    concentration: 'pass'
  },
  documents: {
    invoice: true,
    po: true,
    bol: true,
    ucc: true
  }
}, {
  id: 'INV-10234',
  obligor: 'Home Depot',
  supplier: 'New South Construction Supply',
  amount: 89450,
  dueDate: '2025-12-10',
  rating: 'A',
  tenor: 30,
  status: 'pending',
  aiScore: 85,
  riskFlags: [],
  complianceChecks: {
    aml: 'pass',
    sanctions: 'pass',
    creditLimit: 'pass',
    concentration: 'pass'
  },
  documents: {
    invoice: true,
    po: true,
    bol: true,
    ucc: false
  }
}, {
  id: 'INV-10235',
  obligor: 'Costco',
  supplier: 'ZWILLING J.A. Henckels USA',
  amount: 178900,
  dueDate: '2025-12-31',
  rating: 'AA-',
  tenor: 45,
  status: 'ai_verified',
  aiScore: 92,
  riskFlags: [],
  complianceChecks: {
    aml: 'pass',
    sanctions: 'pass',
    creditLimit: 'pass',
    concentration: 'pass'
  },
  documents: {
    invoice: true,
    po: true,
    bol: true,
    ucc: true
  }
}];
export function Audit() {
  const [invoices, setInvoices] = useState<Invoice[]>(INVOICES_DATA);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(INVOICES_DATA[2]);
  const [liveMode, setLiveMode] = useState(true);
  const [verificationMode, setVerificationMode] = useState<'auto' | 'manual'>('auto');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [flagNotes, setFlagNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [stats] = useState({
    totalProcessed: 1247,
    todayProcessed: 43,
    avgTime: 18,
    accuracyRate: 99.2,
    humanReview: 8.3
  });
  const [auditTrail, setAuditTrail] = useState([{
    time: '14:32:18',
    user: 'AI System',
    action: 'Auto-verified INV-10229',
    result: 'PASS'
  }, {
    time: '14:31:45',
    user: 'Sarah Chen',
    action: 'Manual review INV-10228',
    result: 'APPROVED'
  }, {
    time: '14:30:22',
    user: 'AI System',
    action: 'Flagged INV-10227',
    result: 'CONCENTRATION'
  }]);
  const handleApprove = useCallback(() => {
    setShowApprovalModal(false);
    if (selectedInvoice) {
      const now = new Date();
      const timeStr = now.toTimeString().slice(0, 8);
      setAuditTrail(prev => [{
        time: timeStr,
        user: 'Current User',
        action: `Approved ${selectedInvoice.id}`,
        result: 'APPROVED'
      }, ...prev]);
    }
  }, [selectedInvoice]);
  const handleFlag = useCallback(() => {
    setShowFlagModal(true);
  }, []);
  const handleFlagSubmit = useCallback(() => {
    if (!flagReason || !selectedInvoice) return;
    const now = new Date();
    const timeStr = now.toTimeString().slice(0, 8);
    const displayReason = flagReason === 'other' ? flagNotes : flagReason;
    setAuditTrail(prev => [{
      time: timeStr,
      user: 'Current User',
      action: `Flagged ${selectedInvoice.id}: ${displayReason}`,
      result: 'FLAGGED'
    }, ...prev]);
    setShowFlagModal(false);
    setFlagReason('');
    setFlagNotes('');
  }, [selectedInvoice, flagReason, flagNotes]);
  const handleReject = useCallback(() => {
    setShowRejectModal(true);
  }, []);
  const handleRejectSubmit = useCallback(() => {
    if (!rejectReason || !selectedInvoice) return;
    const now = new Date();
    const timeStr = now.toTimeString().slice(0, 8);
    setAuditTrail(prev => [{
      time: timeStr,
      user: 'Current User',
      action: `Rejected ${selectedInvoice.id}: ${rejectReason}`,
      result: 'REJECTED'
    }, ...prev]);
    setShowRejectModal(false);
    setRejectReason('');
  }, [selectedInvoice, rejectReason]);
  return <div className="min-h-screen bg-white" data-id="element-675">
      {/* Header - Larger and more prominent */}
      <div className="border-b border-gray-300 bg-white" data-id="element-676">
        <div className="px-6 py-6" data-id="element-677">
          <div className="flex items-center justify-between" data-id="element-678">
            <div className="flex items-center gap-6" data-id="element-679">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-680">Audit</h1>
              <div className="flex items-center gap-2" data-id="element-681">
                <div className={`w-2 h-2 rounded-full ${liveMode ? 'bg-gray-900' : 'bg-gray-400'}`} data-id="element-682" />
                <span className="text-xs text-gray-600 uppercase font-medium" data-id="element-683">
                  {liveMode ? 'LIVE' : 'PAUSED'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-684">
              <span className="px-4 py-2 bg-gray-900 text-white text-xs font-medium uppercase" data-id="element-685">
                MODE: {verificationMode.toUpperCase()}
              </span>
              <button onClick={() => setVerificationMode(v => v === 'auto' ? 'manual' : 'auto')} className="px-4 py-2 border border-gray-400 text-xs font-medium uppercase hover:bg-gray-50" data-id="element-686">
                SWITCH MODE
              </button>
              <button onClick={() => setLiveMode(!liveMode)} className="px-4 py-2 bg-gray-800 text-white text-xs font-medium uppercase hover:bg-gray-900" data-id="element-687">
                {liveMode ? 'STOP' : 'START'} LIVE
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Stats Bar - Bigger and more prominent */}
      <div className="bg-gray-800 text-white border-b-4 border-gray-900" data-id="element-688">
        <div className="px-6 py-6" data-id="element-689">
          <div className="grid grid-cols-5 gap-8" data-id="element-690">
            <div className="text-center" data-id="element-691">
              <div className="text-xs text-gray-400 uppercase mb-2 font-medium" data-id="element-692">
                TOTAL VERIFIED
              </div>
              <div className="text-3xl font-bold mb-1" data-id="element-693">
                {stats.totalProcessed.toLocaleString()}
              </div>
            </div>
            <div className="text-center" data-id="element-694">
              <div className="text-xs text-gray-400 uppercase mb-2 font-medium" data-id="element-695">
                TODAY
              </div>
              <div className="flex items-center justify-center gap-2" data-id="element-696">
                <span className="text-3xl font-bold" data-id="element-697">
                  {stats.todayProcessed}
                </span>
                <span className="text-sm text-green-400 font-medium" data-id="element-698">+12%</span>
              </div>
            </div>
            <div className="text-center" data-id="element-699">
              <div className="text-xs text-gray-400 uppercase mb-2 font-medium" data-id="element-700">
                AVG TIME
              </div>
              <div className="text-3xl font-bold mb-1" data-id="element-701">{stats.avgTime}s</div>
            </div>
            <div className="text-center" data-id="element-702">
              <div className="text-xs text-gray-400 uppercase mb-2 font-medium" data-id="element-703">
                ACCURACY
              </div>
              <div className="text-3xl font-bold mb-1" data-id="element-704">
                {stats.accuracyRate}%
              </div>
            </div>
            <div className="text-center" data-id="element-705">
              <div className="text-xs text-gray-400 uppercase mb-2 font-medium" data-id="element-706">
                HUMAN REVIEW
              </div>
              <div className="flex items-center justify-center gap-2" data-id="element-707">
                <span className="text-3xl font-bold" data-id="element-708">{stats.humanReview}%</span>
                <span className="text-sm text-green-400 font-medium" data-id="element-709">
                  -2.1%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="flex h-[calc(100vh-200px)]" data-id="element-710">
        {/* Left Panel */}
        <div className="w-1/2 border-r border-gray-300 overflow-y-auto" data-id="element-711">
          {/* Queue Header */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200" data-id="element-712">
            <div className="flex items-center justify-between" data-id="element-713">
              <h2 className="text-xs font-bold uppercase" data-id="element-714">
                VERIFICATION QUEUE
              </h2>
              <div className="flex items-center gap-2" data-id="element-715">
                <span className="text-[10px] text-gray-600" data-id="element-716">3 PENDING</span>
                <span className="text-[10px] text-gray-600" data-id="element-717">2 AI VERIFIED</span>
                <span className="text-[10px] text-gray-600" data-id="element-718">1 IN REVIEW</span>
              </div>
            </div>
          </div>
          {/* Invoice List */}
          <div data-id="element-719">
            {invoices.map(invoice => <div key={invoice.id} onClick={() => setSelectedInvoice(invoice)} className={`px-6 py-4 border-b border-gray-100 cursor-pointer transition-all ${selectedInvoice?.id === invoice.id ? 'bg-gray-100 border-l-4 border-l-gray-900' : 'hover:bg-gray-50'}`} data-id="element-720">
                <div className="flex items-center gap-3 mb-2" data-id="element-721">
                  <span className="text-sm font-bold" data-id="element-722">{invoice.id}</span>
                  <span className="px-2 py-0.5 bg-gray-900 text-white text-[9px] font-bold" data-id="element-723">
                    {invoice.rating}
                  </span>
                  <span className="px-2 py-0.5 bg-gray-600 text-white text-[9px]" data-id="element-724">
                    AI: {invoice.aiScore}%
                  </span>
                  <span className={`px-2 py-0.5 text-[9px] font-medium ${invoice.status === 'ai_verified' ? 'bg-gray-200 text-gray-700' : invoice.status === 'pending' ? 'bg-gray-100 text-gray-600' : invoice.status === 'human_review' ? 'bg-gray-300 text-gray-800' : 'bg-gray-100 text-gray-600'}`} data-id="element-725">
                    {invoice.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-[11px] text-gray-600" data-id="element-726">
                  <div data-id="element-727">
                    <span className="text-gray-400" data-id="element-728">OBLIGOR:</span>{' '}
                    {invoice.obligor}
                  </div>
                  <div data-id="element-729">
                    <span className="text-gray-400" data-id="element-730">AMOUNT:</span> $
                    {invoice.amount.toLocaleString()}
                  </div>
                  <div data-id="element-731">
                    <span className="text-gray-400" data-id="element-732">SUPPLIER:</span>{' '}
                    {invoice.supplier}
                  </div>
                  <div data-id="element-733">
                    <span className="text-gray-400" data-id="element-734">TENOR:</span>{' '}
                    {invoice.tenor} days
                  </div>
                </div>
                {invoice.riskFlags.length > 0 && <div className="mt-2 text-[10px] text-gray-700" data-id="element-735">
                    ⚠ {invoice.riskFlags.join(', ')}
                  </div>}
              </div>)}
          </div>
          {/* Compliance Matrix - More subtle */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-736">
            <h3 className="text-xs font-bold uppercase mb-3" data-id="element-737">
              REAL-TIME COMPLIANCE MATRIX
            </h3>
            <div className="grid grid-cols-4 gap-3" data-id="element-738">
              <div className="text-center p-3 bg-white border border-gray-300" data-id="element-739">
                <div className="text-[10px] text-gray-600 uppercase" data-id="element-740">
                  AML/KYC
                </div>
                <div className="text-xl font-bold" data-id="element-741">42</div>
                <div className="text-[9px] text-gray-600 uppercase" data-id="element-742">PASS</div>
              </div>
              <div className="text-center p-3 bg-white border border-gray-300" data-id="element-743">
                <div className="text-[10px] text-gray-600 uppercase" data-id="element-744">
                  SANCTIONS
                </div>
                <div className="text-xl font-bold" data-id="element-745">42</div>
                <div className="text-[9px] text-gray-600 uppercase" data-id="element-746">PASS</div>
              </div>
              <div className="text-center p-3 bg-gray-50 border border-gray-400" data-id="element-747">
                <div className="text-[10px] text-gray-600 uppercase" data-id="element-748">
                  CREDIT LIMITS
                </div>
                <div className="text-xl font-bold" data-id="element-749">39</div>
                <div className="text-[9px] text-gray-700 uppercase font-medium" data-id="element-750">
                  WARNING
                </div>
              </div>
              <div className="text-center p-3 bg-gray-100 border border-gray-500" data-id="element-751">
                <div className="text-[10px] text-gray-600 uppercase" data-id="element-752">
                  CONCENTRATION
                </div>
                <div className="text-xl font-bold" data-id="element-753">35</div>
                <div className="text-[9px] text-gray-800 uppercase font-medium" data-id="element-754">
                  FAIL
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Right Panel */}
        <div className="w-1/2 flex flex-col" data-id="element-755">
          {selectedInvoice && <>
              {/* Verification Interface */}
              <div className="flex-1 overflow-y-auto" data-id="element-756">
                <div className="px-6 py-3 bg-gray-800 text-white" data-id="element-757">
                  <div className="flex items-center justify-between" data-id="element-758">
                    <h2 className="text-xs font-bold uppercase" data-id="element-759">
                      VERIFICATION INTERFACE
                    </h2>
                    <button className="text-white hover:text-gray-300" data-id="element-760">
                      ✕
                    </button>
                  </div>
                </div>
                <div className="p-6 space-y-6" data-id="element-761">
                  {/* Invoice Header */}
                  <div data-id="element-762">
                    <div className="flex items-center justify-between mb-4" data-id="element-763">
                      <span className="text-lg font-bold" data-id="element-764">
                        {selectedInvoice.id}
                      </span>
                      <div className="flex items-center gap-2" data-id="element-765">
                        <span className="px-2 py-1 bg-gray-900 text-white text-[10px] font-bold" data-id="element-766">
                          {selectedInvoice.rating}
                        </span>
                        <span className="px-2 py-1 bg-gray-600 text-white text-[10px]" data-id="element-767">
                          AI: {selectedInvoice.aiScore}%
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-768">
                      <div data-id="element-769">
                        <span className="text-gray-500" data-id="element-770">Obligor:</span>
                        <div className="font-semibold" data-id="element-771">
                          {selectedInvoice.obligor}
                        </div>
                      </div>
                      <div data-id="element-772">
                        <span className="text-gray-500" data-id="element-773">Supplier:</span>
                        <div className="font-semibold" data-id="element-774">
                          {selectedInvoice.supplier}
                        </div>
                      </div>
                      <div data-id="element-775">
                        <span className="text-gray-500" data-id="element-776">Amount:</span>
                        <div className="font-semibold" data-id="element-777">
                          ${selectedInvoice.amount.toLocaleString()}
                        </div>
                      </div>
                      <div data-id="element-778">
                        <span className="text-gray-500" data-id="element-779">Due Date:</span>
                        <div className="font-semibold" data-id="element-780">
                          {selectedInvoice.dueDate}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Compliance Checks - Minimal design */}
                  <div data-id="element-781">
                    <h3 className="text-[11px] font-bold text-gray-700 uppercase mb-3" data-id="element-782">
                      COMPLIANCE CHECKS
                    </h3>
                    <div className="grid grid-cols-2 gap-2" data-id="element-783">
                      {Object.entries(selectedInvoice.complianceChecks).map(([key, status]) => <div key={key} className={`px-3 py-2 border text-[11px] font-medium uppercase ${status === 'pass' ? 'bg-white border-gray-300 text-gray-700' : status === 'pending' ? 'bg-gray-50 border-gray-300 text-gray-500' : 'bg-gray-100 border-gray-400 text-gray-800'}`} data-id="element-784">
                            <span className="mr-2" data-id="element-785">
                              {status === 'pass' ? '✓' : status === 'pending' ? '○' : '✗'}
                            </span>
                            {key.replace(/([A-Z])/g, ' $1').toUpperCase()}
                          </div>)}
                    </div>
                  </div>
                  {/* Document Verification - Minimal */}
                  <div data-id="element-786">
                    <h3 className="text-[11px] font-bold text-gray-700 uppercase mb-3" data-id="element-787">
                      DOCUMENT VERIFICATION
                    </h3>
                    <div className="grid grid-cols-2 gap-2" data-id="element-788">
                      {Object.entries(selectedInvoice.documents).map(([key, present]) => <div key={key} className={`px-3 py-2 border text-[11px] font-medium uppercase ${present ? 'bg-white border-gray-300 text-gray-700' : 'bg-gray-50 border-gray-300 text-gray-400'}`} data-id="element-789">
                            <span className="mr-2" data-id="element-790">□</span>
                            {key.toUpperCase()}
                            {present && <span className="float-right" data-id="element-791">✓</span>}
                          </div>)}
                    </div>
                  </div>
                  {/* AI Analysis - Professional */}
                  <div className="border border-gray-300 bg-gray-50 p-4" data-id="element-792">
                    <div className="flex items-center gap-2 mb-2" data-id="element-793">
                      <span className="text-[11px] font-bold text-gray-700 uppercase" data-id="element-794">
                        AI ANALYSIS
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed" data-id="element-795">
                      Invoice verified with {selectedInvoice.aiScore}%
                      confidence. All compliance checks passed. Document
                      verification shows UCC filing pending. No risk flags
                      detected. Recommended for approval.
                    </p>
                  </div>
                  {/* Ready Status */}
                  {selectedInvoice.complianceChecks.aml === 'pass' && selectedInvoice.complianceChecks.sanctions === 'pass' && <div className="px-4 py-3 bg-white border border-gray-400" data-id="element-796">
                        <span className="text-sm font-medium text-gray-800" data-id="element-797">
                          ✓ Ready for Approval
                        </span>
                      </div>}
                  {/* Action Buttons - Professional */}
                  <div className="flex gap-3 pt-2" data-id="element-798">
                    <button onClick={() => setShowApprovalModal(true)} className="flex-1 px-4 py-2.5 bg-gray-900 text-white text-xs font-bold uppercase hover:bg-gray-800" data-id="element-799">
                      ✓ APPROVE
                    </button>
                    <button onClick={handleFlag} className="flex-1 px-4 py-2.5 border border-gray-400 text-gray-700 text-xs font-bold uppercase hover:bg-gray-50" data-id="element-800">
                      ○ FLAG
                    </button>
                    <button onClick={handleReject} className="flex-1 px-4 py-2.5 border border-gray-400 text-gray-700 text-xs font-bold uppercase hover:bg-gray-50" data-id="element-801">
                      ✕ REJECT
                    </button>
                  </div>
                  <div className="text-[10px] text-gray-500 text-center pt-3 border-t" data-id="element-802">
                    All actions are logged and require Level 2 approval for
                    overrides
                  </div>
                </div>
              </div>
              {/* Audit Trail - Bottom */}
              <div className="border-t border-gray-300 bg-gray-50" data-id="element-803">
                <div className="px-6 py-3" data-id="element-804">
                  <h3 className="text-xs font-bold uppercase mb-3" data-id="element-805">
                    AUDIT TRAIL
                  </h3>
                  <div className="space-y-1 max-h-32 overflow-y-auto" data-id="element-806">
                    {auditTrail.map((entry, i) => <div key={i} className="flex items-center gap-4 text-[11px] font-mono" data-id="element-807">
                        <span className="text-gray-500" data-id="element-808">{entry.time}</span>
                        <span className="font-medium w-24" data-id="element-809">{entry.user}</span>
                        <span className="flex-1 text-gray-600" data-id="element-810">
                          {entry.action}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-[9px] font-semibold uppercase" data-id="element-811">
                          {entry.result}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>
            </>}
        </div>
      </div>
      {/* Approval Modal */}
      {showApprovalModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-812">
          <div className="bg-white border border-gray-400 shadow-xl max-w-md w-full" data-id="element-813">
            <div className="px-6 py-4 border-b border-gray-300" data-id="element-814">
              <h3 className="text-sm font-semibold" data-id="element-815">Confirm Approval</h3>
            </div>
            <div className="px-6 py-4" data-id="element-816">
              <p className="text-sm text-gray-700" data-id="element-817">
                Are you sure you want to approve invoice {selectedInvoice?.id}?
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-300 flex justify-end gap-3" data-id="element-818">
              <button onClick={() => setShowApprovalModal(false)} className="px-4 py-2 border border-gray-400 text-sm text-gray-700 hover:bg-gray-100" data-id="element-819">
                Cancel
              </button>
              <button onClick={handleApprove} className="px-4 py-2 bg-gray-900 text-white text-sm hover:bg-gray-800" data-id="element-820">
                Confirm
              </button>
            </div>
          </div>
        </div>}
      {/* Flag Modal */}
      {showFlagModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-821">
          <div className="bg-white border border-gray-400 shadow-xl max-w-md w-full" data-id="element-822">
            <div className="px-6 py-4 border-b border-gray-300" data-id="element-823">
              <h3 className="text-sm font-semibold" data-id="element-824">
                Flag Invoice {selectedInvoice?.id}
              </h3>
            </div>
            <div className="px-6 py-4" data-id="element-825">
              <label className="block text-xs font-medium text-gray-700 mb-2 uppercase" data-id="element-826">
                Flag Reason
              </label>
              <select value={flagReason} onChange={e => setFlagReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-500" data-id="element-827">
                <option value="" data-id="element-828">Select reason...</option>
                <option value="concentration" data-id="element-829">Concentration Risk</option>
                <option value="verification" data-id="element-830">Verification Failed</option>
                <option value="duplicate" data-id="element-831">Duplicate Invoice</option>
                <option value="fraud" data-id="element-832">Suspected Fraud</option>
                <option value="documentation" data-id="element-833">Missing Documentation</option>
                <option value="other" data-id="element-834">Other (specify below)</option>
              </select>
              <label className="block text-xs font-medium text-gray-700 mt-4 mb-2 uppercase" data-id="element-835">
                Additional Notes
              </label>
              <textarea value={flagNotes} onChange={e => setFlagNotes(e.target.value)} placeholder="Enter additional details..." className="w-full px-3 py-2 border border-gray-300 text-sm h-20 focus:outline-none focus:border-gray-500" data-id="element-836" />
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200" data-id="element-837">
                <p className="text-xs text-amber-800" data-id="element-838">
                  ⚠️ This will escalate to Level 2 review. SLA: 4 hours.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-300 flex justify-between" data-id="element-839">
              <button onClick={() => {
            setShowFlagModal(false);
            setFlagReason('');
            setFlagNotes('');
          }} className="px-4 py-2 border border-gray-400 text-sm text-gray-700 hover:bg-gray-100" data-id="element-840">
                Cancel
              </button>
              <button onClick={handleFlagSubmit} disabled={!flagReason} className="px-4 py-2 bg-amber-600 text-white text-sm hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed" data-id="element-841">
                Submit Flag
              </button>
            </div>
          </div>
        </div>}
      {/* Reject Modal */}
      {showRejectModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-842">
          <div className="bg-white border border-gray-400 shadow-xl max-w-md w-full" data-id="element-843">
            <div className="px-6 py-4 border-b border-gray-300" data-id="element-844">
              <h3 className="text-sm font-semibold" data-id="element-845">
                Reject Invoice {selectedInvoice?.id}
              </h3>
            </div>
            <div className="px-6 py-4" data-id="element-846">
              <div className="space-y-3 mb-4" data-id="element-847">
                <div className="flex justify-between text-sm" data-id="element-848">
                  <span className="text-gray-600" data-id="element-849">Obligor:</span>
                  <span className="font-medium" data-id="element-850">
                    {selectedInvoice?.obligor}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-851">
                  <span className="text-gray-600" data-id="element-852">Amount:</span>
                  <span className="font-medium" data-id="element-853">
                    ${selectedInvoice?.amount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-854">
                  <span className="text-gray-600" data-id="element-855">Supplier:</span>
                  <span className="font-medium" data-id="element-856">
                    {selectedInvoice?.supplier}
                  </span>
                </div>
              </div>
              <label className="block text-xs font-medium text-gray-700 mb-2 uppercase" data-id="element-857">
                Rejection Reason
              </label>
              <select value={rejectReason} onChange={e => setRejectReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-500" data-id="element-858">
                <option value="" data-id="element-859">Select reason...</option>
                <option value="failed_aml" data-id="element-860">Failed AML Check</option>
                <option value="failed_sanctions" data-id="element-861">Failed Sanctions Check</option>
                <option value="credit_limit" data-id="element-862">Exceeds Credit Limit</option>
                <option value="invalid_documents" data-id="element-863">Invalid Documentation</option>
                <option value="duplicate" data-id="element-864">Duplicate Invoice</option>
                <option value="fraud" data-id="element-865">Confirmed Fraud</option>
              </select>
              <div className="mt-4 p-3 bg-red-50 border border-red-200" data-id="element-866">
                <p className="text-xs text-red-800 font-medium" data-id="element-867">
                  ⚠️ WARNING: This action cannot be undone.
                </p>
                <p className="text-xs text-red-700 mt-1" data-id="element-868">
                  Invoice will be permanently rejected and supplier will be
                  notified.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-300 flex justify-between" data-id="element-869">
              <button onClick={() => {
            setShowRejectModal(false);
            setRejectReason('');
          }} className="px-4 py-2 border border-gray-400 text-sm text-gray-700 hover:bg-gray-100" data-id="element-870">
                Cancel
              </button>
              <button onClick={handleRejectSubmit} disabled={!rejectReason} className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed" data-id="element-871">
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>}
    </div>;
}